 import type { User } from '@/lib/user';

export interface AuthSessionUser {
  email: string;
  name?: string;
  tenantId?: string;
  oid?: string;
  dbUser?: User;
}

export const AUTH_SESSION_EXPIRED_CODE = 'AUTH_SESSION_EXPIRED';

type AuthSessionExpiredListener = () => void;

const authSessionExpiredListeners = new Set<AuthSessionExpiredListener>();

export function subscribeToAuthSessionExpiry(listener: AuthSessionExpiredListener): () => void {
  authSessionExpiredListeners.add(listener);
  return () => authSessionExpiredListeners.delete(listener);
}

export function notifyAuthSessionExpired(): void {
  authSessionExpiredListeners.forEach(listener => {
    listener();
  });
}

export function getCsrfToken(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )crimson_csrf=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export const CSRF_HEADER = 'X-CSRF-Token';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * CSRF header for a given HTTP method. The BFF only enforces CSRF on unsafe
 * methods, so safe methods (and requests made before the cookie exists) get an
 * empty object.
 */
export function csrfHeaders(method = 'GET'): Record<string, string> {
  if (!UNSAFE_METHODS.has(method.toUpperCase())) return {};
  const token = getCsrfToken();
  return token ? { [CSRF_HEADER]: token } : {};
}

/**
 * fetch() against the BFF: always sends the session cookie and attaches the CSRF
 * header for unsafe methods. Returns the raw Response so callers keep control of
 * status handling (e.g. treating 401 as "logged out" rather than an error).
 *
 * Use this for the cases axios can't cover — server-sent event streams and the
 * /api/auth/* endpoints. Everything else should go through `backendClient`.
 */
export function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  return fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      ...csrfHeaders(method),
      ...init.headers,
    },
  });
}

/**
 * Reads a string `detail` field from a JSON error response (the FastAPI
 * convention), falling back to the supplied message when absent or unparseable.
 */
export async function responseDetail(response: Response, fallback: string): Promise<string> {
  const data = await response.json().catch(() => null);
  return typeof data?.detail === 'string' ? data.detail : fallback;
}
